import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, SelectPicker, Tag } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, PaginationFilterResult } from '@/models/core.model';
import {
  EnergyHubCategoryList, EnergyHubList as EnergyHubListModel, EnergyHubPaginationFilter, EnergyHubStatusList, formatHkd,
} from '@/models/energy-hub.model';
import { getPagedEnergyHubs } from '@/services/energy-hub.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import PageContent from '@/components/PageContent';
import { jsDateToZone } from '@/utils/date-time';
import EnergyHubStatusTag from './energy-hub-status-tag';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const EnergyHubList: React.FC = () => {
  const [filterReq, setFilterReq] = useState<EnergyHubPaginationFilter>(new EnergyHubPaginationFilter());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<EnergyHubListModel[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: EnergyHubPaginationFilter) => {
    setLoading(true);
    try {
      const result = await getPagedEnergyHubs(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ ...pre, pageNumber: result.pageNumber, totalRecords: result.totalRecords, pageSize: result.pageSize }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching energy hubs: ${errorModel?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <Button appearance="primary" onClick={() => nav('/energy-hubs/new')}>Add Energy Hub</Button>
          <Button appearance="ghost" onClick={() => nav('/energy-hub-receipts')}>Receipt Queue</Button>
        </Stack>
        <Stack spacing={6}>
          <SelectPicker
            label="Category"
            data={EnergyHubCategoryList}
            searchable={false}
            value={filterReq.category ?? null}
            onChange={val => setFilterReq(pre => ({ ...pre, pageNumber: 1, category: val ?? undefined }))}
          />
          <SelectPicker
            label="Status"
            data={EnergyHubStatusList}
            searchable={false}
            value={filterReq.status ?? null}
            onChange={val => setFilterReq(pre => ({ ...pre, pageNumber: 1, status: val ?? undefined }))}
          />
          <Input
            placeholder="Keyword"
            value={filterReq.keyword ?? ''}
            onChange={val => setFilterReq(pre => ({ ...pre, pageNumber: 1, keyword: val }))}
          />
        </Stack>
      </Stack>

      <Table height={Math.max(getHeight(window) - 350, 400)} data={items} loading={loading}>
        <Column flexGrow={0.4} align="center">
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={2} fullText>
          <HeaderCell>Name</HeaderCell>
          <Cell>
            {(row: EnergyHubListModel) => (
              <>
                {row.name}
                {row.isFoundingHub && <Tag size="sm" color="cyan" style={{ marginLeft: 8 }}>Founding</Tag>}
              </>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>Category</HeaderCell>
          <Cell>{(row: EnergyHubListModel) => EnergyHubCategoryList.find(c => c.value == row.category)?.label ?? row.category}</Cell>
        </Column>
        <Column flexGrow={0.8} align="center">
          <HeaderCell>Status</HeaderCell>
          <Cell>{(row: EnergyHubListModel) => <EnergyHubStatusTag status={row.status} />}</Cell>
        </Column>
        <Column flexGrow={1} align="right">
          <HeaderCell>Credit</HeaderCell>
          <Cell>
            {(row: EnergyHubListModel) => (
              <span style={{ color: row.creditBalance < 0 ? '#e55' : undefined, fontWeight: 600 }}>{formatHkd(row.creditBalance)}</span>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center">
          <HeaderCell>Free Until</HeaderCell>
          <Cell fullText>{(row: EnergyHubListModel) => jsDateToZone(row.freeUntil)}</Cell>
        </Column>
        <Column flexGrow={1} align="center">
          <HeaderCell>Created At</HeaderCell>
          <Cell fullText>{(row: EnergyHubListModel) => jsDateToZone(row.createdAt)}</Cell>
        </Column>
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(row: EnergyHubListModel) => (
              <IconButton onClick={() => nav(`/energy-hubs/${row.id}`)} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={val => setFilterReq(pre => ({ ...pre, pageNumber: val }))}
        onChangeLimit={val => setFilterReq(pre => ({ ...pre, pageSize: val }))}
      />
    </PageContent>
  );
};

export default EnergyHubList;
